import type { DocumentCategory } from "../documents";
import type { CdMap } from "../components/dossier/cdHerkenning";
import { isSysteembestand } from "../components/dossier/cdHerkenning";
import type { CdZip } from "../components/dossier/cdZip";
import { BEELDVORMING_MAP } from "../components/dossier/cdZip";
import type {
  UploadBatchBestandStand,
  UploadBatchStand,
} from "../services/uploadBatchService";
import type { OnderbrokenBatch, QueuedUploadItem } from "./uploadQueueReducer";
import { laatsteWijziging, wijzigdatumVanBestand } from "./uploadQueueReducer";

/** Een bestand uit de opnieuw gekozen map. */
export interface GekozenBestand {
  relativePath: string;
  file: File;
}

const slash = (p: string) => p.replace(/\\/g, "/");
const isOpen = (b: UploadBatchBestandStand) =>
  b.status === "WACHT" || b.status === "BEZIG";

/** Hetzelfde pad, of de gekozen map was een bovenliggende map. */
export function padPast(gekozen: string, opgeslagen: string): boolean {
  const g = slash(gekozen);
  const o = slash(opgeslagen);
  return g === o || g.endsWith(`/${o}`);
}

/** Laatste segment van een pad: de mapnaam van een cd. */
export const mapnaamVan = (bronPad: string): string =>
  slash(bronPad).split("/").filter(Boolean).pop() ?? bronPad;

/**
 * De bestanden onder de cd-map, met paden die weer met de mapnaam beginnen.
 * `bronPad` is het pad uit de drop ("CD", of "Patient/CD1" voor een sub-cd):
 * op dat volle pad, zodat twee cd's met dezelfde mapnaam niet samenvallen
 * (A2-4). Koos de gebruiker de cd-map zelf, dan beginnen de paden met de
 * mapnaam; dat telt alleen als het volle pad nergens voorkomt.
 */
export function cdBestandenUit(
  gekozen: GekozenBestand[],
  bronPad: string,
): GekozenBestand[] {
  return zoekCdBestanden(gekozen, bronPad).bestanden;
}

/**
 * Als cdBestandenUit, plus `meerdere`: het cd-pad staat meer dan eens in de
 * gekozen map (A3-3). Dan zijn er geen bestanden en vraagt de webapp de
 * cd-map zelf te kiezen (B1-3).
 */
export function zoekCdBestanden(
  gekozen: GekozenBestand[],
  bronPad: string,
): { bestanden: GekozenBestand[]; meerdere: boolean } {
  const bron = slash(bronPad);
  const mapnaam = mapnaamVan(bron);
  const uitPad = (prefix: string, toegestaanBinnenin: boolean) => {
    const voor = `${prefix}/`;
    const uit: GekozenBestand[] = [];
    // Wat er vóór de cd-map staat: meer dan één variant betekent dat het
    // cd-pad meer dan eens in de gekozen map staat (A3-3).
    const ervoor = new Set<string>();
    for (const g of gekozen) {
      const pad = slash(g.relativePath);
      let rest: string | null = null;
      if (pad.startsWith(voor)) {
        rest = pad.slice(voor.length);
        ervoor.add("");
      } else if (toegestaanBinnenin && pad.includes(`/${voor}`)) {
        const idx = pad.indexOf(`/${voor}`);
        rest = pad.slice(idx + voor.length + 1);
        ervoor.add(pad.slice(0, idx + 1));
      }
      if (rest !== null) {
        uit.push({ relativePath: `${mapnaam}/${rest}`, file: g.file });
      }
    }
    return { uit, dubbel: ervoor.size > 1 };
  };
  const opVolPad = uitPad(bron, true);
  // Dubbel: nooit twee cd's in één zip. De gebruiker kiest dan de cd-map zelf.
  if (opVolPad.dubbel) return { bestanden: [], meerdere: true };
  if (opVolPad.uit.length > 0 || bron === mapnaam) {
    return { bestanden: opVolPad.uit, meerdere: false };
  }
  const opMapnaam = uitPad(mapnaam, false);
  return opMapnaam.dubbel
    ? { bestanden: [], meerdere: true }
    : { bestanden: opMapnaam.uit, meerdere: false };
}

/** Waarom een gekozen map niets opleverde, als de webapp dat preciezer weet. */
export type KoppelReden = "meerdere_cd_mappen";

/**
 * Koppelt een onderbroken batch aan de map die de gebruiker opnieuw koos.
 * Gewone bestanden: op pad en grootte. Een cd: dezelfde bestanden opnieuw
 * gepland; dezelfde grootte = dezelfde bytes, dus de oude sessie gaat verder.
 * In de oude sessie alleen als ook de wijzigdatum gelijk is (L8): een
 * opnieuw opgeslagen bestand van dezelfde grootte begint een nieuwe sessie.
 */
export async function koppelAanGekozenMap(
  stand: UploadBatchStand,
  gekozen: GekozenBestand[],
  planCd: (cd: CdMap) => Promise<CdZip>,
): Promise<{
  items: QueuedUploadItem[];
  nietGevonden: UploadBatchBestandStand[];
  reden?: KoppelReden;
}> {
  const items: QueuedUploadItem[] = [];
  const nietGevonden: UploadBatchBestandStand[] = [];
  let reden: KoppelReden | undefined;
  for (const b of stand.bestanden.filter(isOpen)) {
    const basis = {
      naam: b.naam,
      category: (b.category ?? "OVERIG") as DocumentCategory,
      relatiefPad: b.relatiefPad,
      bestandId: b.id,
    };
    const hervat = { uploadUrl: b.uploadUrl, blobName: b.blobName };
    if (b.cdZip) {
      const gevonden = zoekCdBestanden(gekozen, b.relatiefPad);
      if (gevonden.meerdere) {
        // Niet als "niet gevonden" melden: de cd blijft open en hervatbaar.
        reden = "meerdere_cd_mappen";
        continue;
      }
      const bestanden = gevonden.bestanden.filter(
        (g) => !isSysteembestand(g.relativePath),
      );
      if (bestanden.length === 0) {
        nietGevonden.push(b);
        continue;
      }
      const cd = await planCd({
        mapnaam: mapnaamVan(b.relatiefPad),
        bronPad: b.relatiefPad,
        bestanden,
      });
      items.push({
        ...basis,
        cd,
        targetFolder: b.folderPath ?? BEELDVORMING_MAP,
        hervat:
          cd.grootte === b.grootte &&
          laatsteWijziging(bestanden) === b.laatstGewijzigdOp
            ? hervat
            : undefined,
      });
      continue;
    }
    const hit = gekozen.find(
      (g) =>
        padPast(g.relativePath, b.relatiefPad) && g.file.size === b.grootte,
    );
    if (!hit) {
      nietGevonden.push(b);
      continue;
    }
    items.push({
      ...basis,
      file: hit.file,
      targetFolder: b.folderPath ?? undefined,
      hervat:
        wijzigdatumVanBestand(hit.file) === b.laatstGewijzigdOp
          ? hervat
          : undefined,
    });
  }
  return { items, nietGevonden, ...(reden ? { reden } : {}) };
}

/** Een batch die niet af is: BEZIG met minstens één open bestand. */
export function alsOnderbroken(
  dossierKenmerk: string,
  stand: UploadBatchStand,
): OnderbrokenBatch | null {
  const open = stand.bestanden.filter(isOpen);
  if (stand.status !== "BEZIG" || open.length === 0) return null;
  return {
    dossierKenmerk,
    batchId: stand.id,
    open: open.length,
    heeftCd: open.some((b) => b.cdZip),
  };
}

/** Per dossier de batches die dit tabblad startte; overleeft herladen en opnieuw inloggen. */
const VOORVOEGSEL = "koc:upload-batches:";
type Opgeslagen = { batchId: string; bestandIds: string[] };

function lees(kenmerk: string): Opgeslagen[] {
  try {
    const lijst = JSON.parse(
      window.localStorage.getItem(VOORVOEGSEL + kenmerk) ?? "[]",
    );
    return Array.isArray(lijst)
      ? lijst.filter((b) => typeof b?.batchId === "string")
      : [];
  } catch {
    return [];
  }
}

function schrijf(kenmerk: string, lijst: Opgeslagen[]): void {
  try {
    if (lijst.length > 0) {
      window.localStorage.setItem(VOORVOEGSEL + kenmerk, JSON.stringify(lijst));
    } else {
      window.localStorage.removeItem(VOORVOEGSEL + kenmerk);
    }
  } catch {
    // Geen opslag (privévenster): de upload loopt, alleen hervatten na herladen kan dan niet.
  }
}

export function onthoudBatch(
  kenmerk: string,
  batchId: string,
  bestandIds: string[],
): void {
  schrijf(kenmerk, [
    ...lees(kenmerk).filter((b) => b.batchId !== batchId),
    { batchId, bestandIds },
  ]);
}

export function vergeetBatch(kenmerk: string, batchId: string): void {
  schrijf(
    kenmerk,
    lees(kenmerk).filter((b) => b.batchId !== batchId),
  );
}

export function leesOpgeslagenBatches(): {
  dossierKenmerk: string;
  batchId: string;
}[] {
  const uit: { dossierKenmerk: string; batchId: string }[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const sleutel = window.localStorage.key(i);
      if (!sleutel?.startsWith(VOORVOEGSEL)) continue;
      const kenmerk = sleutel.slice(VOORVOEGSEL.length);
      for (const b of lees(kenmerk)) {
        uit.push({ dossierKenmerk: kenmerk, batchId: b.batchId });
      }
    }
  } catch {
    // Geen opslag: niets te hervatten.
  }
  return uit;
}

/**
 * Hartslag per batch (B1-1, B1b): het tabblad dat een batch bezit (geregistreerd,
 * wachtend of lopend), schrijft elke 15 s een tijdstip in localStorage, met zijn
 * eigen tabblad-id. Andere tabbladen tonen zo'n batch niet als onderbroken en
 * hervatten of stoppen hem niet zolang die hartslag vers is, zodat niemand
 * dezelfde Graph-sessie tegelijk gebruikt. Het tabblad-id is nieuw per keer
 * laden: bij herladen wist pagehide de eigen hartslagen.
 */
const HARTSLAG_VOORVOEGSEL = "koc:upload-hartslag:";
export const HARTSLAG_INTERVAL_MS = 15_000;
/**
 * Zo lang na de laatste slag geldt een batch nog als "loopt elders". Ruim:
 * een verborgen tabblad krijgt van Chrome soms maar één timertik per minuut,
 * dus 150 s overleeft twee gemiste tikken (B1b-3).
 */
export const HARTSLAG_GELDIG_MS = 150_000;

/** Id van deze keer laden van de pagina (B1b-4); nooit gedeeld met een gedupliceerd tabblad. */
const tabbladId = `${Date.now().toString(36)}-${Math.random()
  .toString(36)
  .slice(2)}`;

type Slag = { tab?: unknown; op?: unknown };

function leesSlag(batchId: string): Slag | null {
  try {
    const ruw = window.localStorage.getItem(HARTSLAG_VOORVOEGSEL + batchId);
    return ruw ? (JSON.parse(ruw) as Slag) : null;
  } catch {
    return null;
  }
}

export function zetHartslag(batchId: string, nu = Date.now()): void {
  try {
    window.localStorage.setItem(
      HARTSLAG_VOORVOEGSEL + batchId,
      JSON.stringify({ tab: tabbladId, op: nu }),
    );
  } catch {
    // Geen opslag: dan ziet een ander tabblad de batch niet als lopend.
  }
}

/** Is de hartslag van deze batch (nog) van dit tabblad? Voor claimen en controleren bij hervatten. */
export function hartslagVanDitTabblad(batchId: string): boolean {
  return leesSlag(batchId)?.tab === tabbladId;
}

/** Wist de hartslag, maar alleen als hij van dit tabblad is: die van een ander tabblad blijft staan. */
export function wisEigenHartslag(batchId: string): void {
  if (!hartslagVanDitTabblad(batchId)) return;
  try {
    window.localStorage.removeItem(HARTSLAG_VOORVOEGSEL + batchId);
  } catch {
    // Geen opslag.
  }
}

/** Bezit een ander tabblad deze batch nog (verse hartslag van een ander tabblad-id)? */
export function looptElders(batchId: string, nu = Date.now()): boolean {
  const slag = leesSlag(batchId);
  return (
    typeof slag?.op === "number" &&
    slag.tab !== tabbladId &&
    nu - slag.op < HARTSLAG_GELDIG_MS
  );
}

/** Ruimt verlopen of kapotte hartslagen op (een vastgelopen tabblad liet ze achter, B1b-4). */
export function ruimOudeHartslagenOp(nu = Date.now()): void {
  try {
    const weg: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const sleutel = window.localStorage.key(i);
      if (!sleutel?.startsWith(HARTSLAG_VOORVOEGSEL)) continue;
      const slag = leesSlag(sleutel.slice(HARTSLAG_VOORVOEGSEL.length));
      if (typeof slag?.op !== "number" || nu - slag.op >= HARTSLAG_GELDIG_MS) {
        weg.push(sleutel);
      }
    }
    weg.forEach((s) => window.localStorage.removeItem(s));
  } catch {
    // Geen opslag: niets op te ruimen.
  }
}
